import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { patientsData, getPatientHistory, getPatientAppointments } from "@/data/mockData";
import {
  Search,
  User,
  ChevronRight,
  Filter,
  Users,
  Calendar,
  Activity
} from "lucide-react";
import { cn } from "@/lib/utils";

export const PatientsListPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [genderFilter, setGenderFilter] = useState("all");
  const [view, setView] = useState("grid"); // grid or table

  // Filter patients
  const filteredPatients = patientsData.filter(patient => {
    const matchesSearch = 
      patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.fiscalCode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGender = genderFilter === "all" || patient.gender === genderFilter;
    return matchesSearch && matchesGender;
  });

  const PatientCard = ({ patient }) => {
    const history = getPatientHistory(patient.id);
    const appointments = getPatientAppointments(patient.id);
    const upcomingApts = appointments.filter(a => a.status === "scheduled").length;

    return (
      <Card
        className="cursor-pointer hover:shadow-lg transition-all hover:border-primary/50"
        onClick={() => navigate(`/dashboard/patients/${patient.id}`)}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <Avatar className="h-12 w-12 border-2 border-primary/20">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {patient.name.split(" ").map(n => n[0]).join("")}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold truncate">{patient.name}</h3>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </div>
              <p className="text-sm text-muted-foreground font-mono">
                {patient.fiscalCode}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <Badge variant="outline" className="text-xs">
                  {patient.age} anni
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {patient.gender === "M" ? "Maschio" : "Femmina"}
                </Badge>
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Activity className="h-3 w-3" />
                  {history.length} esami
                </span>
                {upcomingApts > 0 && (
                  <span className="flex items-center gap-1 text-primary">
                    <Calendar className="h-3 w-3" />
                    {upcomingApts} prenotati
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Pazienti</h1>
          <p className="text-muted-foreground">Gestisci l'anagrafica pazienti</p>
        </div>
        <Badge variant="secondary" className="w-fit gap-2">
          <Users className="h-4 w-4" />
          {patientsData.length} pazienti registrati
        </Badge>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per nome o codice fiscale..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Genere" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="M">Maschi</SelectItem>
                <SelectItem value="F">Femmine</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {filteredPatients.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <User className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">Nessun paziente trovato</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPatients.map((patient) => (
            <PatientCard key={patient.id} patient={patient} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PatientsListPage;
